import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Drawer,
    Box,
    Typography,
    FormControl,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    InputLabel,
    CircularProgress,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CargoManagementQuery from '../../../services/query/cargoManagement/CargoManagementQuery.services';
import { SCUIButton } from '../../../atomic/atoms/SCUIButton/SCUIButton';
import Swal from 'sweetalert2';
import { FDCM } from '../fastDeliveryCargoManagement.classes';

interface Company {
    value: number;
    text: string;
}

interface AddDeliveryDrawerProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: any) => Promise<void>;
    loading: boolean;
    companyList: Company[];
}

const AddDeliveryDrawer: React.FC<AddDeliveryDrawerProps> = ({ open, onClose, onSave, loading, companyList }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        shippingCompanyId: '',
        cityId: '',
        countyId: '',
        neighborhoodId: '',
        isActiveForTomorrowDelivery: false,
        isActiveForExpressDelivery: false,
        isActiveForTodayDelivery: false,
    });

    const [cityList, setCityList] = useState<any[]>([]);
    const [countyList, setCountyList] = useState<any[]>([]);
    const [neighborhoodList, setNeighborhoodList] = useState<any[]>([]);

    const GetCityList = async () => {
        try {
            let params = {
                addressEntityTypeId: 3,
                countryId: 1,
            };
            CargoManagementQuery.CityList(params).then((res: any) => {
                if (res.data.addressEntities) {
                    let parseData = res.data.addressEntities.map((item: any) => {
                        return {
                            value: item.addressEntityId,
                            text: item.name,
                        };
                    });
                    setCityList(parseData);
                } else {
                    Swal.fire('Service Error', res.errorMessage, 'error');
                }
            });
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetCountyList = async () => {
        try {
            let params = {
                ParentAddressEntityId: formData.cityId,
                countryId: 1,
            };
            if (formData.cityId !== '') {
                CargoManagementQuery.SubAddressEntities(params).then((res: any) => {
                    if (res.data.addressEntities) {
                        let parseData = res.data.addressEntities.map((item: any) => {
                            return {
                                value: item.addressEntityId,
                                text: item.name,
                            };
                        });
                        setCountyList(parseData);
                    } else {
                        Swal.fire('Service Error', res.errorMessage, 'error');
                    }
                });
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    const GetNeighborhoodList = async () => {
        try {
            let params = {
                ParentAddressEntityId: formData.countyId,
                countryId: 1,
            };
            if (formData.countyId !== '') {
                CargoManagementQuery.SubAddressEntities(params).then((res: any) => {
                    if (res.data.addressEntities) {
                        let parseData = res.data.addressEntities.map((item: any) => {
                            return {
                                value: item.addressEntityId,
                                text: item.name,
                            };
                        });
                        setNeighborhoodList(parseData);
                    } else {
                        Swal.fire('Service Error', res.errorMessage, 'error');
                    }
                });
            }
        } catch (error: any) {
            Swal.fire('Error', error.toString(), 'error');
        }
    };

    useEffect(() => {
        GetCityList();
    }, []);

    useEffect(() => {
        if (formData.cityId) {
            GetCountyList();
        }
    }, [formData.cityId]);

    useEffect(() => {
        if (formData.countyId) {
            GetNeighborhoodList();
        }
    }, [formData.countyId]);

    const handleChange = (field: string) => (event: any) => {
        const value = event.target.value;
        setFormData((prev) => {
            const newData = {
                ...prev,
                [field]: value,
            };

            if (field === 'cityId') {
                newData.countyId = '';
                newData.neighborhoodId = '';
                setCountyList([]);
                setNeighborhoodList([]);
            } else if (field === 'countyId') {
                newData.neighborhoodId = '';
                setNeighborhoodList([]);
            }

            return newData;
        });
    };

    const handleSwitchChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [field]: event.target.checked,
        });
    };

    const handleSave = async () => {
        try {
            await onSave(formData);
            Swal.fire({
                icon: 'success',
                title: t('general.success'),
                text: t('fastDeliveryCargoManagement.addSuccess'),
                showConfirmButton: false,
                timer: 1500,
            });
            handleClose();
        } catch (error: any) {
            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: error.message || t('fastDeliveryCargoManagement.addError'),
                confirmButtonText: t('general.ok'),
            });
        }
    };

    const isFormValid = () => {
        return formData.shippingCompanyId && formData.cityId && formData.countyId && formData.neighborhoodId;
    };

    const handleClose = () => {
        setFormData({
            shippingCompanyId: '',
            cityId: '',
            countyId: '',
            neighborhoodId: '',
            isActiveForTomorrowDelivery: false,
            isActiveForExpressDelivery: false,
            isActiveForTodayDelivery: false,
        });
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            slotProps={{
                root: {
                    sx: { width: '400px' },
                },
                paper: {
                    className: FDCM.addDrawerRoot,
                },
            }}
        >
            <Box className={FDCM.addDrawerContent} sx={{ p: 3 }}>
                <Box className={FDCM.addDrawerHeader} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6">{t('fastDeliveryCargoManagement.addNew')}</Typography>
                    <IconButton className={FDCM.inputButtonCloseAddDrawer} onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <FormControl className={FDCM.addShippingCompanyFieldWrap} fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('fastDeliveryCargoManagement.shippingCompany')}</InputLabel>
                    <Select
                        className={FDCM.inputSelectAddShippingCompany}
                        value={formData.shippingCompanyId}
                        onChange={handleChange('shippingCompanyId')}
                        label={t('fastDeliveryCargoManagement.shippingCompany')}
                    >
                        {companyList
                            .filter((company: Company) => company.text != t('general.all'))
                            .map((company) => (
                                <MenuItem key={company.value} value={company.value}>
                                    {company.text}
                                </MenuItem>
                            ))}
                    </Select>
                </FormControl>

                <FormControl className={FDCM.addCityFieldWrap} fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{t('fastDeliveryCargoManagement.city')}</InputLabel>
                    <Select
                        className={FDCM.inputSelectAddCity}
                        value={formData.cityId}
                        onChange={handleChange('cityId')}
                        label={t('fastDeliveryCargoManagement.city')}
                    >
                        {cityList.map((city) => (
                            <MenuItem key={city.value} value={city.value}>
                                {city.text}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {formData.cityId && (
                    <FormControl className={FDCM.addDistrictFieldWrap} fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t('fastDeliveryCargoManagement.district')}</InputLabel>
                        <Select
                            className={FDCM.inputSelectAddDistrict}
                            value={formData.countyId}
                            onChange={handleChange('countyId')}
                            label={t('fastDeliveryCargoManagement.district')}
                        >
                            {countyList.map((county) => (
                                <MenuItem key={county.value} value={county.value}>
                                    {county.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {formData.countyId && (
                    <FormControl className={FDCM.addNeighborhoodFieldWrap} fullWidth sx={{ mb: 2 }}>
                        <InputLabel>{t('fastDeliveryCargoManagement.neighborhood')}</InputLabel>
                        <Select
                            className={FDCM.inputSelectAddNeighborhood}
                            value={formData.neighborhoodId}
                            onChange={handleChange('neighborhoodId')}
                            label={t('fastDeliveryCargoManagement.neighborhood')}
                        >
                            {neighborhoodList.map((neighborhood) => (
                                <MenuItem key={neighborhood.value} value={neighborhood.value}>
                                    {neighborhood.text}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <FormControlLabel
                    control={
                        <Switch
                            className={FDCM.inputSwitchAddTomorrowDelivery}
                            checked={formData.isActiveForTomorrowDelivery}
                            onChange={handleSwitchChange('isActiveForTomorrowDelivery')}
                        />
                    }
                    label={t('fastDeliveryCargoManagement.tomorrowDelivery')}
                    sx={{ mb: 2 }}
                />

                <FormControlLabel
                    control={
                        <Switch
                            className={FDCM.inputSwitchAddExpressDelivery}
                            checked={formData.isActiveForExpressDelivery}
                            onChange={handleSwitchChange('isActiveForExpressDelivery')}
                        />
                    }
                    label={t('fastDeliveryCargoManagement.expressDelivery')}
                    sx={{ mb: 2 }}
                />

                <FormControlLabel
                    control={
                        <Switch
                            className={FDCM.inputSwitchAddTodayDelivery}
                            checked={formData.isActiveForTodayDelivery}
                            onChange={handleSwitchChange('isActiveForTodayDelivery')}
                        />
                    }
                    label={t('fastDeliveryCargoManagement.todayDelivery')}
                    sx={{ mb: 3 }}
                />

                <Box className={FDCM.addDrawerActionsWrap} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <SCUIButton
                        className={FDCM.inputButtonAddCancel}
                        onClick={handleClose}
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('general.cancel')}
                        disabled={loading}
                    />
                    <SCUIButton
                        className={FDCM.inputButtonAddSave}
                        onClick={handleSave}
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('general.save')}
                        disabled={!isFormValid() || loading}
                    >
                        {loading && <CircularProgress size={20} color="inherit" sx={{ ml: 1 }} />}
                    </SCUIButton>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AddDeliveryDrawer;
